/**
 * ResultsDashboardPage -- Wraps Step 6F ResultsDashboard component
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 * Route: /results/:testId
 *
 * Self-fetching, role-adaptive results view with:
 * - Lock states (awaiting client TP, awaiting baseline, dual-lock)
 * - Time Allocation panel
 * - Role-fit alignment for Scenario 4
 * - Baseline readiness for Scenario 5
 * - Per-triad detail table + export
 */

import React, { useEffect, useState } from "react";
import { PageSpinner } from "../../components/PageSpinner";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, BarChart3, Info } from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import { apiClient } from "../../services/apiClient";
import { EmptyState } from "../../components/EmptyState";
import { ResultsExportButton } from "../../components/ResultsExportButton";
import { toDisplayText } from "../../constants/displayText";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Alert, AlertDescription } from "../../components/ui/alert";
import type {
  ActivityTPPoint,
  BaselineReadinessParticipant,
  BucketSummary,
  ResultsData,
  TriadResultRow,
  TriadResultsResponse,
} from "../../types";

function getPrimaryRole(roles: string[]): string {
  if (roles.includes("Test Administrator")) return "Test Administrator";
  if (roles.includes("Client Manager")) return "Client Manager";
  if (roles.includes("Content Admin")) return "Content Admin";
  if (roles.includes("Candidate")) return "Candidate";
  if (roles.includes("Super Admin")) return "Test Administrator";
  return roles[0] ?? "Candidate";
}

// --- Step 3: helpers ---

type ParticipantEntry = {
  participant_id: string;
  name: string;
  email: string;
  status?: string;
};

function getInitials(name: string, email: string, pid: string): string {
  const src = name || email || pid;
  const parts = src.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function buildParticipantList(
  data: ResultsData,
  triadRows: TriadResultRow[],
  activityTpPoints: ActivityTPPoint[]
): ParticipantEntry[] {
  const seen = new Set<string>();
  const list: ParticipantEntry[] = [];

  for (const p of data.participants ?? []) {
    if (!seen.has(p.participant_id)) {
      seen.add(p.participant_id);
      list.push({
        participant_id: p.participant_id,
        name: p.name ?? "",
        email: p.email ?? "",
        status: p.status ?? "",
      });
    }
  }

  for (const row of triadRows) {
    if (!seen.has(row.participant_id)) {
      seen.add(row.participant_id);
      list.push({
        participant_id: row.participant_id,
        name: row.participant_name ?? "",
        email: row.participant_email ?? "",
      });
    }
  }

  for (const pt of activityTpPoints) {
    if (!seen.has(pt.participant_id)) {
      seen.add(pt.participant_id);
      list.push({
        participant_id: pt.participant_id,
        name: pt.participant_name ?? "",
        email: "",
      });
    }
  }

  return list;
}

export function ResultsDashboardPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userRole = getPrimaryRole(user?.roles ?? []);

  const [data, setData] = useState<ResultsData | null>(null);
  const [triadRows, setTriadRows] = useState<TriadResultRow[]>([]);
  const [activityTpPoints, setActivityTpPoints] = useState<ActivityTPPoint[]>([]);
  const [buckets, setBuckets] = useState<BucketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!testId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchResults = apiClient.get<ResultsData>(`/api/results/${testId}`);

    const fetchTriads = apiClient.get<TriadResultsResponse>(`/api/results/${testId}/triads`).catch(() => null);

    Promise.all([fetchResults, fetchTriads])
      .then(([resultsJson, triadsJson]) => {
        if (cancelled) return;
        setData(resultsJson);
        setTriadRows(triadsJson?.rows ?? []);
        setActivityTpPoints(triadsJson?.activity_tp_points ?? []);
        setBuckets(triadsJson?.buckets ?? []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message ?? "Failed to load results");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [testId]);

  // Step 1: role-adaptive back navigation
  const handleBack = () => {
    if (userRole === "Test Administrator") navigate(`/test-admin/tests/${testId}/monitor`);
    else if (userRole === "Candidate") navigate("/candidate/welcome");
    else navigate(-1);
  };

  if (!testId) {
    return (
      <main style={{ minHeight: "600px" }} className="bg-brand-panel p-6">
        <h1 className="text-lg font-semibold text-gray-900 mb-4">Results</h1>
        <div role="alert" className="text-sm text-brand-error">
          No test ID provided.
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main style={{ minHeight: "600px" }} className="bg-brand-panel p-6">
        <h1 className="text-lg font-semibold text-gray-900 mb-4">Results</h1>
        <PageSpinner label="Loading results" />
      </main>
    );
  }

  if (error) {
    return (
      <main style={{ minHeight: "600px" }} className="bg-brand-panel p-6">
        <h1 className="text-lg font-semibold text-gray-900 mb-4">Results</h1>
        <div role="alert" className="p-4 bg-red-50 border border-brand-error/40 rounded text-sm text-brand-error">
          {error}
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main style={{ minHeight: "600px" }} className="bg-brand-panel p-6">
        <h1 className="text-lg font-semibold text-gray-900 mb-4">Results</h1>
        <div role="alert" className="p-4 bg-red-50 border border-brand-error/40 rounded text-sm text-brand-error">
          No results data.
        </div>
      </main>
    );
  }

  const status = data.status;
  // overall_maturity removed from UI — field still in API response but not displayed (single-value
  // summary doesn't represent multi-participant tests correctly)
  const isLocked = data.locked ?? status === "results_locked";
  const lockReason = data.lock_reason;
  const missingInputs = data.missing_inputs ?? [];
  const scenarioType = data.scenario_type;
  const baselineReadiness = data.baseline_readiness;
  // Candidate progress status keyed by participant_id. The Baseline Readiness
  // section (Sc5) reuses this so each candidate's row shows the same status
  // pill the Participants accordion does — both derive from data.participants.
  const statusByParticipantId = new Map(
    (data.participants ?? []).map((p) => [p.participant_id, p.status ?? ""]),
  );
  const showDownload = userRole === "Client Manager" || userRole === "Test Administrator";

  // Step 1: locked state now gets the full back button + dynamic header
  if (isLocked) {
    return (
      <main style={{ minHeight: "600px" }} className="bg-brand-panel p-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 border-brand-steel/40"
            onClick={handleBack}
            aria-label="Back"
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-gray-900">Back to monitoring</h1>
          </div>
        </div>
        <div className="p-6 bg-brand-highlight/40 border border-brand-warning/50 rounded-lg" role="alert">
          <p className="text-sm font-semibold text-amber-800 mb-2">
            Results are locked — awaiting client input.
          </p>
          {missingInputs.length > 0 ? (
            <>
              <p className="text-xs text-amber-800/90 mb-1">The following input(s) are still required:</p>
              <ul className="list-disc list-inside text-sm text-amber-800">
                {missingInputs.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-amber-800">
              {lockReason === "awaiting_role_tp"
                ? "Awaiting client Role Time Prioritization input."
                : "Pending client input."}
            </p>
          )}
        </div>
      </main>
    );
  }

  // Step 3: build participant list
  const participantList = buildParticipantList(data, triadRows, activityTpPoints);

  return (
    <main style={{ minHeight: "600px" }} className="bg-brand-panel p-6">
      {/* Step 1: Single back button + dynamic test name header */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 border-brand-steel/40"
          onClick={handleBack}
          aria-label="Back"
        >
          <ArrowLeft size={16} />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-gray-900">{"Back to monitoring"}</h1>
        </div>
      </div>

      {triadRows.length === 0 && activityTpPoints.length === 0 && (
        <EmptyState
          icon={BarChart3}
          title="No results available yet"
          description="Results will appear here once participants have completed their assessments."
        />
      )}

      {data.scoring_diagnostics && <ScoringDiagnosticsBanner diagnostics={data.scoring_diagnostics} />}

      {/* Scenario 5: Baseline readiness — candidate triads vs baseline reference */}
      {scenarioType === "5" && baselineReadiness && (
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Baseline Readiness</h2>
          {baselineReadiness.status === "not_linked" ? (
            <div className="p-4 bg-brand-highlight/40 border border-brand-warning/50 rounded-lg">
              <p className="text-sm text-amber-800">
                {baselineReadiness.message ?? "Baseline comparison is not linked."}
              </p>
            </div>
          ) : (
            <>
              {(baselineReadiness.baseline_name || baselineReadiness.version_warning?.message) && (
                <p className="text-xs text-brand-steel mb-3">
                  {baselineReadiness.baseline_name && (
                    <>
                      Each candidate triad is compared against baseline{" "}
                      <span className="font-medium text-gray-900">{baselineReadiness.baseline_name}</span>.{" "}
                    </>
                  )}
                  {baselineReadiness.version_warning?.message}
                </p>
              )}
              {baselineReadiness.tp_divergence_warning?.message && (
                <Alert variant="warning" className="mb-3">
                  <AlertTriangle size={16} aria-hidden="true" />
                  <AlertDescription>
                    {baselineReadiness.tp_divergence_warning.message}
                  </AlertDescription>
                </Alert>
              )}
              {baselineReadiness.participants.length === 0 ? (
                <div className="p-4 bg-brand-highlight/40 border border-brand-warning/50 rounded-lg">
                  <p className="text-sm text-amber-800">No participants have completed the assessment yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {baselineReadiness.participants.map((p) => (
                    <BaselineComparisonTable
                      key={p.participant_id}
                      participant={p}
                      status={statusByParticipantId.get(p.participant_id)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Step 4 / §2.5: Sc4+Sc5 shared Time Allocation block (above accordions) */}
      {(scenarioType === "4" || scenarioType === "5") && activityTpPoints.length > 0 && (
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-gray-900 mb-2">Time Allocation</h2>
          <p className="text-xs text-brand-steel mb-3">
            Time allocation set by the Client Manager for this assessment. Activities with points &gt; 0 are
            highlighted.
          </p>
          <Card className="border-brand-steel/50 bg-white shadow-sm">
            <CardContent className="px-5 py-1">
              <TimeAllocationSection
                buckets={buckets}
                points={activityTpPoints.filter((p) => p.participant_id === activityTpPoints[0].participant_id)}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Participant Accordions */}
      {participantList.length > 0 && (
        <>
          <h2 className="text-sm font-semibold text-gray-900 mb-1 pt-1">Participants</h2>
          <p className="text-xs text-brand-steel mb-2">
            Select a participant to view their time allocation and per-triad assessment results.
          </p>
        </>
      )}
      {participantList.length > 0 && (
        <Accordion
          type="multiple"
          className="space-y-2 mb-4"
          defaultValue={participantList.length === 1 ? [participantList[0].participant_id] : undefined}
        >
          {participantList.map((participant) => {
            const participantTriadRows = triadRows.filter((r) => r.participant_id === participant.participant_id);
            const participantTpPoints = activityTpPoints.filter((p) => p.participant_id === participant.participant_id);
            const triadCount = participantTriadRows.length;
            const showTP =
              buckets.length > 0 && participantTpPoints.length > 0 && scenarioType !== "4" && scenarioType !== "5";
            const isExpired = participant.status === "Expired";
            const isInProgress = participant.status === "In Progress";
            const isPaused = participant.status === "Paused";
            const isNotStarted = participant.status === "Invited" || participant.status === "Not Invited";
            const initials = getInitials(participant.name, participant.email, participant.participant_id);

            return (
              <AccordionItem
                key={participant.participant_id}
                value={participant.participant_id}
                className="border border-brand-steel/50 rounded-lg bg-white shadow-sm"
              >
                <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-brand-panel/50 rounded-lg data-[state=open]:rounded-b-none">
                  <div className="flex items-center gap-3 text-left w-full">
                    <div
                      className="flex items-center justify-center rounded-full bg-brand-navy text-white text-xs font-semibold shrink-0"
                      style={{ width: 36, height: 36 }}
                      aria-hidden="true"
                    >
                      {initials}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {participant.name || participant.email || participant.participant_id}
                      </p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {participant.email && <p className="text-xs text-brand-steel">{participant.email}</p>}
                        {participant.status && (
                          <>
                            {participant.email && <span className="text-xs text-brand-steel/50">·</span>}
                            <ParticipantStatusBadge status={participant.status} />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-4 border-t border-brand-steel/50">
                  {isNotStarted ? (
                    // Not-started candidates have no TP submission and no triads yet —
                    // skip both the Time Allocation block and the Per-Triad Detail
                    // heading entirely and show a single notice instead.
                    <div className="pt-3">
                      <Alert variant="info">
                        <Info size={16} aria-hidden="true" />
                        <AlertDescription>Candidate has not started the assessment yet.</AlertDescription>
                      </Alert>
                    </div>
                  ) : (
                    <>
                      {showTP && (
                        <div className="pt-3 mb-4">
                          <h2 className="text-sm font-semibold text-gray-900 mb-1">Time Allocation</h2>
                          <p className="text-xs text-brand-steel mb-3">
                            Points the candidate allocated to each activity during Time Prioritization. Activities with
                            points &gt; 0 are highlighted.
                          </p>
                          <TimeAllocationSection buckets={buckets} points={participantTpPoints} />
                        </div>
                      )}

                      {/* Heading + explainer always render — the table (or its empty-state
                          alert) shows for any started candidate (in progress, paused, or
                          completed); only not-started skips this block entirely. */}
                      <div className={!showTP ? "pt-3" : "pt-5 border-t border-brand-steel/15"}>
                        <h2 className="text-sm font-semibold text-gray-900 mb-2">Per-Triad Detail</h2>
                        <p className="text-xs text-brand-steel mb-3">
                          Each row is one triad — identified by (Bucket, Activity, Faculty Level). Pathway is decided
                          from the Faculty maturity; F/B/T scores are not aggregated. Cells marked &quot;Skipped&quot;
                          were not re-served because the same competency appeared in an earlier triad.
                        </p>
                        {/* Gating is on isNotStarted alone (handled above), so any
                            started candidate reaches here — In Progress and Paused
                            included, as well as any status we haven't anticipated.
                            Whether the table renders depends only on whether the
                            candidate actually has triad rows, never on their status:
                            an In Progress candidate with results must see them. */}
                        {isExpired && (
                          <Alert variant="warning" className="mb-3">
                            <AlertTriangle size={16} aria-hidden="true" />
                            <AlertDescription>
                              This candidate's assessment expired before completion.
                            </AlertDescription>
                          </Alert>
                        )}
                        {triadCount > 0 ? (
                          <TriadResultsSection rows={participantTriadRows} />
                        ) : (
                          <Alert variant="info">
                            <Info size={16} aria-hidden="true" />
                            <AlertDescription>No triad results recorded for this candidate yet.</AlertDescription>
                          </Alert>
                        )}
                      </div>
                    </>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      {/* Step 6 / §2.7: Level Legends always after the accordion block */}
      {triadRows.length > 0 && <LevelLegendsSection />}

      {/* Export button: scope-aware CSV / zip download */}
      {showDownload && (
        <div className="flex justify-end">
          <ResultsExportButton testId={testId} />
        </div>
      )}
    </main>
  );
}

const COMPETENCY_LEVELS: Array<{ key: string; label: string; meaning: string }> = [
  { key: "L1", label: "Far", meaning: "Demonstrated occasionally and still being explored." },
  { key: "L2", label: "Familiar", meaning: "Demonstrated with increasing confidence in familiar situations." },
  { key: "L3", label: "Known", meaning: "Demonstrated consistently across situations." },
  { key: "L4", label: "Core", meaning: "Naturally demonstrated and influential in driving outcomes." },
];

const MATURITY_LEVELS: Array<{ key: string; label: string; meaning: string }> = [
  {
    key: "M1",
    label: "Experimenting",
    meaning: "Beginning to develop the capability; exploring and applying it in limited situations.",
  },
  {
    key: "M2",
    label: "Skilled",
    meaning: "Demonstrates the capability effectively in familiar situations with growing confidence.",
  },
  {
    key: "M3",
    label: "Competent",
    meaning: "Applies the capability consistently and effectively across a wide range of situations.",
  },
  {
    key: "M4",
    label: "Mature",
    meaning:
      "Exhibits the capability as a well-developed strength, demonstrating expertise, influence, and sustained effectiveness.",
  },
];

function LevelLegendsSection() {
  return (
    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <LevelLegend
        title="Competency Level"
        subtitle="Used for Faculty, Behaviour and Technique Maturity columns."
        entries={COMPETENCY_LEVELS}
      />
      <LevelLegend title="Maturity Level" subtitle="Used for the Triad Maturity column." entries={MATURITY_LEVELS} />
    </div>
  );
}

function LevelLegend({
  title,
  subtitle,
  entries,
}: {
  title: string;
  subtitle: string;
  entries: Array<{ key: string; label: string; meaning: string }>;
}) {
  return (
    <div className="bg-white border border-brand-steel/50 rounded-lg shadow-sm overflow-hidden">
      <div className="bg-brand-panel/50 px-4 py-2 border-b border-brand-steel/50">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-brand-steel mt-0.5">{subtitle}</p>
      </div>
      <table className="w-full text-sm border-collapse">
        <tbody>
          {entries.map((e) => (
            <tr key={e.key} className="border-b border-brand-steel/30 last:border-b-0">
              <td className="px-4 py-2 align-top w-32">
                <MaturityBadge level={e.key} />
              </td>
              <td className="px-4 py-2 align-top text-brand-steel">{e.meaning}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Maturity badge mirroring the activity table's color scheme.
 * Displays the alias from displayText.ts (L1→Far, L2→Familiar, L3→Known, L4→Core;
 * M1→Experimenting, M2→Skilled, M3→Competent, M4→Mature) so the page matches
 * the customer-facing labels. */
function MaturityBadge({ level }: { level: string | null }) {
  const display = level ? toDisplayText(level) : "—";
  const cls =
    level === "L4" || level === "M4"
      ? "bg-green-200 text-green-900 font-semibold"
      : level === "L3" || level === "M3"
      ? "bg-green-100 text-brand-success"
      : level === "L2" || level === "M2"
      ? "bg-amber-100 text-amber-800"
      : level === "L1" || level === "M1"
      ? "bg-red-100 text-brand-error"
      : "bg-brand-steel/15 text-brand-steel";
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls}`}>{display}</span>;
}

/** Gap pill: candidate triad relative to the baseline reference maturity. */
function GapBadge({ gap }: { gap: "at" | "above" | "below" | null }) {
  if (!gap) return <span className="text-brand-steel italic">—</span>;
  const cls =
    gap === "at"
      ? "bg-green-100 text-green-800"
      : gap === "above"
      ? "bg-blue-100 text-blue-800"
      : "bg-amber-100 text-amber-800";
  const label = gap === "at" ? "At Baseline" : gap === "above" ? "Above" : "Development Area";
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

/** Scenario 5: one participant's candidate-vs-baseline comparison. Each row is
 * a single triad with its own M score (no activity-level rollup) gapped against
 * the baseline's per-activity reference maturity. */
/** Candidate progress pill — shared by the Participants accordion trigger and
 * the Scenario 5 Baseline Readiness header so both render status identically.
 * "Invited" is shown as "Not started" (the candidate was invited but hasn't begun). */
function ParticipantStatusBadge({ status }: { status?: string }) {
  if (status === "Completed") {
    return (
      <span className="flex items-center gap-1 text-xs text-green-700">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" aria-hidden="true" />
        Completed
      </span>
    );
  }
  if (status === "In Progress") {
    return (
      <span className="flex items-center gap-1 text-xs text-amber-800">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" aria-hidden="true" />
        In Progress
      </span>
    );
  }
  if (status === "Paused") {
    return (
      <span className="flex items-center gap-1 text-xs text-blue-800">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" aria-hidden="true" />
        Paused
      </span>
    );
  }
  if (status === "Invited") {
    return (
      <span className="flex items-center gap-1 text-xs text-brand-steel">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-steel/50 shrink-0" aria-hidden="true" />
        Not started
      </span>
    );
  }
  if (status === "Not Invited") {
    return (
      <span className="flex items-center gap-1 text-xs text-brand-steel">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-steel/50 shrink-0" aria-hidden="true" />
        Not Invited
      </span>
    );
  }
  if (status === "Expired") {
    return (
      <span className="flex items-center gap-1 text-xs text-brand-error">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-error shrink-0" aria-hidden="true" />
        Expired
      </span>
    );
  }
  return null;
}

function BaselineComparisonTable({
  participant,
  status,
}: {
  participant: BaselineReadinessParticipant;
  status?: string;
}) {
  return (
    <div className="bg-white border border-brand-steel/50 rounded-lg shadow-sm overflow-hidden">
      <div className="bg-brand-panel/50 px-4 py-2 border-b border-brand-steel/50 flex items-center gap-2 flex-wrap">
        <p className="text-sm font-semibold text-gray-900">{participant.participant_name}</p>
        {status && (
          <>
            <span className="text-xs text-brand-steel/50" aria-hidden="true">·</span>
            <ParticipantStatusBadge status={status} />
          </>
        )}
      </div>
      {participant.comparison.length === 0 ? (
        <p className="px-4 py-3 text-xs text-brand-steel italic">No scored triads to compare yet.</p>
      ) : (
        <div className="p-4 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-brand-panel text-xs font-semibold text-gray-700 uppercase tracking-wider">
                <th scope="col" className="px-3 py-2 text-left">
                  Activity
                </th>
                <th scope="col" className="px-3 py-2 text-left">
                  Faculty Level
                </th>
                <th scope="col" className="px-3 py-2 text-center">
                  Candidate Maturity
                </th>
                <th scope="col" className="px-3 py-2 text-center">
                  Baseline Maturity
                </th>
                <th scope="col" className="px-3 py-2 text-center">
                  Gap
                </th>
              </tr>
            </thead>
            <tbody>
              {participant.comparison.map((row, idx) => (
                <tr
                  key={`${row.activity_id}-${row.faculty_level}-${idx}`}
                  className="odd:bg-white even:bg-brand-panel/50 hover:bg-brand-panel transition-colors"
                >
                  <td className="px-3 py-2 text-gray-900">{row.activity_name}</td>
                  <td className="px-3 py-2 text-gray-900">{row.faculty_level ?? "—"}</td>
                  <td className="px-3 py-2 text-center">
                    <MaturityBadge level={row.candidate_maturity} />
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.baseline_maturity ? (
                      <MaturityBadge level={row.baseline_maturity} />
                    ) : (
                      <span className="text-brand-steel italic">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <GapBadge gap={row.gap} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Status pill for a single F/B/T cell. */
function CellStatusBadge({ status }: { status: TriadResultRow["faculty"]["status"] }) {
  if (status === "answered") return null;
  if (status === "skipped_already_served") {
    return (
      <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-brand-steel/15 text-brand-steel/70 uppercase tracking-wider">
        Skipped
      </span>
    );
  }
  if (status === "score_failed") {
    return (
      <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-brand-error uppercase tracking-wider">
        Score failed
      </span>
    );
  }
  return (
    <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-brand-panel text-brand-steel/60 uppercase tracking-wider">
      Not served
    </span>
  );
}

/** Step 2: Refactored TimeAllocationSection — accepts a single participant's points.
 * Grouping loop removed; bucket total shown right-aligned in header. */
function TimeAllocationSection({ buckets, points }: { buckets: BucketSummary[]; points: ActivityTPPoint[] }) {
  const pointsByActivity = new Map<string, number>();
  for (const p of points) {
    pointsByActivity.set(p.activity_id, p.tp_points);
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {buckets.map((bucket) => {
        const bucketTotal = bucket.activities.reduce((s, a) => s + (pointsByActivity.get(a.activity_id) ?? 0), 0);
        return (
          <div key={bucket.bucket_id} className="bg-white overflow-hidden">
            <div className="bg-brand-panel px-4 py-2 flex items-baseline justify-between">
              <p className="text-sm font-semibold text-gray-900 mb-0!">{bucket.bucket_name}</p>
              <p className="tabular-nums text-xs text-gray-700 font-bold">{bucketTotal} pts</p>
            </div>
            {bucket.activities.length === 0 ? (
              <p className="px-3 py-3 text-xs text-brand-steel italic">No activities</p>
            ) : (
              <ul>
                {bucket.activities.map((activity) => {
                  const tp = pointsByActivity.get(activity.activity_id) ?? 0;
                  const assessed = tp > 0;
                  return (
                    <li
                      key={activity.activity_id}
                      className={`flex items-baseline gap-3 px-3 py-1.5 text-sm ${
                        assessed ? "text-brand-crimson font-medium" : "text-gray-500"
                      }`}
                    >
                      <span className="flex-1">{activity.activity_name}</span>
                      <span
                        className={`tabular-nums text-xs ${
                          assessed ? "text-brand-crimson font-semibold" : "text-brand-steel"
                        }`}
                      >
                        {tp} pts
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Admin-only banner — shown when the scoring pipeline dropped or failed to
 * score one or more responses. Only Test Admin / Content Admin / Super Admin
 * viewers receive ``scoring_diagnostics`` in the API response; Candidates
 * never see this banner. */
function ScoringDiagnosticsBanner({ diagnostics }: { diagnostics: NonNullable<ResultsData["scoring_diagnostics"]> }) {
  const parts: string[] = [];
  if (diagnostics.no_rule_match) parts.push(`${diagnostics.no_rule_match} response(s) failed to match a scoring rule`);
  if (diagnostics.partial) parts.push(`${diagnostics.partial} partial response(s) skipped`);
  if (diagnostics.invalid) parts.push(`${diagnostics.invalid} invalidated response(s) skipped`);
  if (diagnostics.dropped_dupes) parts.push(`${diagnostics.dropped_dupes} duplicate response(s) collapsed`);
  if (!parts.length) return null;
  return (
    <div className="mb-4 p-4 bg-red-50 border border-brand-error/40 rounded-lg">
      <p className="text-sm font-medium text-brand-error mb-1">Scoring diagnostics</p>
      <ul className="text-xs text-brand-error/80 list-disc list-inside space-y-0.5">
        {parts.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      {diagnostics.no_rule_match ? (
        <p className="text-xs text-brand-error/80 mt-2">
          Likely cause: the version&apos;s scoring rule set is missing a fallback rule. Review the rule list in Content
          Studio.
        </p>
      ) : null}
    </div>
  );
}

/** Step 5: Refactored TriadResultsSection — accepts a single participant's rows.
 * Grouping loop and participant name label removed; accordion trigger is the label. */
function TriadResultsSection({ rows }: { rows: TriadResultRow[] }) {
  return (
    <div className="bg-white overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-brand-panel text-xs font-semibold text-gray-700 uppercase tracking-wider">
            <th scope="col" className="px-3 py-2 text-left">
              Bucket
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              Activity
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              Faculty Level
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              Faculty Name
            </th>
            <th scope="col" className="px-3 py-2 text-center">
              Faculty Maturity
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              Behaviour Name
            </th>
            <th scope="col" className="px-3 py-2 text-center">
              Behaviour Maturity
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              Technique Name
            </th>
            <th scope="col" className="px-3 py-2 text-center">
              Technique Maturity
            </th>
            <th scope="col" className="px-3 py-2 text-center">
              Triad Maturity
            </th>
            <th scope="col" className="px-3 py-2 text-left">
              Definition of Triad
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TriadDetailRow key={`${row.activity_id}-${row.faculty_level}`} row={row} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TriadDetailRow({ row }: { row: TriadResultRow }) {
  return (
    <tr className="odd:bg-white even:bg-brand-panel/50 hover:bg-brand-panel transition-colors">
      <td className="px-3 py-2 text-gray-900 align-top">
        {row.bucket_name || <span className="text-brand-steel italic">—</span>}
      </td>
      <td className="px-3 py-2 text-gray-900 align-top">{row.activity_name}</td>
      <td className="px-3 py-2 text-gray-900 align-top">{row.faculty_level}</td>
      <td className="px-3 py-2 text-gray-900 align-top">
        {row.faculty.competency_name || <span className="text-brand-steel italic">—</span>}
      </td>
      <td className="px-3 py-2 text-center align-top">
        <MaturityBadge level={row.faculty.maturity} />
        <CellStatusBadge status={row.faculty.status} />
      </td>
      <td className="px-3 py-2 text-gray-900 align-top">
        {row.behaviour.competency_name || <span className="text-brand-steel italic">—</span>}
      </td>
      <td className="px-3 py-2 text-center align-top">
        <MaturityBadge level={row.behaviour.maturity} />
        <CellStatusBadge status={row.behaviour.status} />
      </td>
      <td className="px-3 py-2 text-gray-900 align-top">
        {row.technique.competency_name || <span className="text-brand-steel italic">—</span>}
      </td>
      <td className="px-3 py-2 text-center align-top">
        <MaturityBadge level={row.technique.maturity} />
        <CellStatusBadge status={row.technique.status} />
      </td>
      <td className="px-3 py-2 text-center align-top">
        <MaturityBadge level={row.triad_maturity} />
      </td>
      <td className="px-3 py-2 text-gray-900 align-top">
        {row.definition || <span className="text-brand-steel italic">—</span>}
      </td>
    </tr>
  );
}

export default ResultsDashboardPage;
